import { useZones } from 'features/Responder/features/Zones';
import { getZoneLockState } from 'features/Responder/features/Zones';
import { deleteZoneWithConfirmation } from 'features/Responder/features/Zones';
import useResponderDialog from 'features/Responder/hooks/useResponderDialog';
import React, { useMemo } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import { useResponderMap } from 'features/Responder/features/ResponderMap/contexts/ResponderMapContext';

const btnStyle = {
  width: '100%',
  height: '40px',
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.25)',
  background: 'rgba(255,255,255,0.05)',
  color: '#fff',
  fontSize: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

const activeStyle = {
  background: '#2e7be7',
  borderColor: '#2e7be7',
};

function TooltipWrap({ text, children, placement = 'top' }) {
  const tooltipId = useMemo(() => `zone-editor-tip-${Math.random().toString(36).slice(2, 10)}`, []);
  return (
    <OverlayTrigger
      placement={placement}
      delay={{ show: 200, hide: 100 }}
      overlay={
        <Tooltip className="responder-tooltip" id={tooltipId}>
          {text}
        </Tooltip>
      }
    >
      <span className="d-inline-flex w-100">{children}</span>
    </OverlayTrigger>
  );
}

function IconButton({ tooltip, onClick, disabled, style, ariaLabel, children }) {
  const resolvedStyle = disabled ? { ...style, pointerEvents: 'none' } : style;
  return (
    <TooltipWrap text={tooltip}>
      <button
        type="button"
        style={resolvedStyle}
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel || tooltip}
        className="w-100"
      >
        {children}
      </button>
    </TooltipWrap>
  );
}

export default function ZoneEditBar() {
  const {
    activeZoneId,
    zones,
    selectedZoneIds,
    updateZone,
    deleteZone,
    zoneEditMode,
    setZoneEditMode,
    zonesEditing,
    undoZoneGeometry,
    redoZoneGeometry,
    lockZones,
  } = useZones();
  const { showZoneEditor, setShowZoneEditor } = useResponderMap();
  const { confirm, showAlert, dialogElement } = useResponderDialog();

  const activeZone = useMemo(() => {
    return Array.isArray(zones) ? zones.find((z) => `${z.id}` === `${activeZoneId}`) : null;
  }, [activeZoneId, zones]);

  const targets = useMemo(() => {
    if (Array.isArray(selectedZoneIds) && selectedZoneIds.length) return selectedZoneIds;
    return activeZoneId != null ? [activeZoneId] : [];
  }, [activeZoneId, selectedZoneIds]);

  const toggleLock = () => {
    if (!targets.length) return;
    const currentlyLocked = activeZone ? !!activeZone.locked : false;
    lockZones(targets, !currentlyLocked);
  };

  const disabled = !zonesEditing || !activeZone;
  const { locked } = getZoneLockState({ zone: activeZone });

  const handleNameChange = (e) => {
    if (!activeZone || locked) return;
    updateZone(activeZone.id, { name: e.target.value });
  };

  const handleColorChange = (e) => {
    if (!activeZone || locked) return;
    updateZone(activeZone.id, { fillColor: e.target.value, strokeColor: e.target.value });
  };

  const handleDelete = async () => {
    await deleteZoneWithConfirmation({ activeZone, zones, deleteZone, confirm, showAlert });
  };

  const swatchStyle = {
    width: 70,
    height: 24,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.2)',
    padding: 3,
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const deleteBtnStyle = {
    boxShadow: 'none',
    background: 'transparent',
    border: 'none',
    padding: 0,
    color: '#FF0000',
  };

  return (
    <>
      <div
        className="trip-tools-panel"
        style={{
          zIndex: 640,
          minWidth: 240,
          maxWidth: 260,
          width: 260,
          position: 'static',
          marginBottom: 0,
        }}
      >
        <div className="trip-tools-header d-flex justify-content-between align-items-center">
          <span>Zone Editor</span>
          <Form.Check
            type="switch"
            id="toggle-zone-editor"
            label=""
            checked={!!showZoneEditor}
            onChange={(e) => setShowZoneEditor(e.target.checked)}
            title="Always show editor"
          />
        </div>
        <div className="d-flex flex-column gap-2 mb-2">
          <input
            type="text"
            className="form-control form-control-sm zone-editor-input"
            placeholder="Zone name"
            value={activeZone?.name || ''}
            onChange={handleNameChange}
            disabled={!activeZone || locked}
          />
          <div className="d-flex align-items-center gap-2">
            <label className="small text-muted mb-0">Color</label>
            <div style={swatchStyle}>
              <input
                type="color"
                className="zone-editor-color form-control form-control-color border-0 p-0"
                value={activeZone?.fillColor || activeZone?.strokeColor || '#f97316'}
                onChange={handleColorChange}
                disabled={!activeZone || locked}
                style={{
                  width: 50,
                  height: 16,
                  padding: 0,
                  background: 'transparent',
                  border: 'none',
                }}
              />
            </div>
            <TooltipWrap text="Delete zone">
              <span className="d-inline-flex ms-auto">
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={handleDelete}
                  disabled={!activeZone}
                  aria-label="Delete zone"
                  style={
                    !activeZone ? { ...deleteBtnStyle, pointerEvents: 'none' } : deleteBtnStyle
                  }
                >
                  <FaTrash />
                </button>
              </span>
            </TooltipWrap>
          </div>
        </div>
        <div
          className="d-grid"
          style={{
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 8,
          }}
        >
          <IconButton
            style={{ ...btnStyle, ...(zoneEditMode === 'select' ? activeStyle : {}) }}
            tooltip="Select and move zone"
            onClick={() => setZoneEditMode('select')}
            disabled={disabled}
          >
            {'\u2196'}
          </IconButton>
          <IconButton
            style={{ ...btnStyle, ...(zoneEditMode === 'reshape' ? activeStyle : {}) }}
            tooltip="Reshape by moving one vertex at a time"
            onClick={() => setZoneEditMode('reshape')}
            disabled={disabled}
          >
            {'\u270e'}
          </IconButton>
          <IconButton
            style={{ ...btnStyle, ...(zoneEditMode === 'resize' ? activeStyle : {}) }}
            tooltip="Resize entire zone"
            onClick={() => setZoneEditMode('resize')}
            disabled={disabled}
          >
            {'\u2922'}
          </IconButton>
          <IconButton
            style={{ ...btnStyle, ...(zoneEditMode === 'addVertex' ? activeStyle : {}) }}
            tooltip="Add vertex"
            onClick={() => setZoneEditMode('addVertex')}
            disabled={disabled}
          >
            {'\uff0b'}
          </IconButton>
          <IconButton
            style={{ ...btnStyle, ...(zoneEditMode === 'eraseVertex' ? activeStyle : {}) }}
            tooltip="Erase vertex"
            onClick={() => setZoneEditMode('eraseVertex')}
            disabled={disabled}
          >
            {'\u232b'}
          </IconButton>
          <IconButton
            style={btnStyle}
            tooltip="Undo"
            onClick={() => {
              if (!activeZoneId) return;
              undoZoneGeometry(activeZoneId);
            }}
            disabled={!zonesEditing || !activeZoneId}
          >
            {'\u21ba'}
          </IconButton>
          <IconButton
            style={btnStyle}
            tooltip="Redo"
            onClick={() => {
              if (!activeZoneId) return;
              redoZoneGeometry(activeZoneId);
            }}
            disabled={!zonesEditing || !activeZoneId}
          >
            {'\u21bb'}
          </IconButton>
          <IconButton
            style={btnStyle}
            tooltip={activeZone?.locked ? 'Unlock' : 'Lock'}
            onClick={toggleLock}
            disabled={!activeZone}
          >
            {activeZone?.locked ? '\uD83D\uDD13' : '\uD83D\uDD12'}
          </IconButton>
        </div>
      </div>
      {dialogElement}
    </>
  );
}


